import { type Dayjs } from "dayjs";

import { type UserRoleEnum } from "@fitness-app/data-models";
import {
  type AutomationMessageType,
  type CalendarEventFilterCondition,
  type FilterOperator,
  type TraineeTagFilterCondition,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import {
  type CycleSurvey,
  type CycleSurveyType,
  type SurveyIntervalUnit,
} from "@fitness-app/data-models/entities/ClientSurvey";
import { type ProgramAutomationTaskScheduleTiming } from "@fitness-app/data-models/entities/ProgramAutomation";

import { type MessageAttachmentsFormValues } from "~/modules/Chat/components/MessageAttachments/MessageAttachmentsFormItems";

export enum FormMode {
  Calendar,
  Template,
  Template_Add,
}

export const weekDayLabels = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

export type WeekdayLabels = (typeof weekDayLabels)[number];

export type CRMTagFilterConditionField = {
  type: "crmTags";
  tags: string[];
  emailClients: string[];
  condition: "hasTag" | "notHasTag";
};

export interface SentTimeConfig {
  mode: "absolute" | "relative";
  avoidSendingOnTheNight?: boolean;
  calculateFullRangeOverNight?: boolean;
}

export interface BaseAutomatedMessagesFormModel extends MessageAttachmentsFormValues {
  id: string | null;
  mode: FormMode;
  name: string;
  sentTimeConfig: SentTimeConfig;
  sentTime: Dayjs;
  messages: string[];
  authorId: string;
  authorName: string;
  authorRole: UserRoleEnum;
  authorAvatar: string | null;
  comment?: string;
  sentDate?: Dayjs;
  saveAsTemplate?: boolean;
  daysOfDelivery?: WeekdayLabels[];
  numberOfRepeats?: number;
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterConditionField)[];
  filterOperator?: FilterOperator;
  messageType?: AutomationMessageType;
  scheduleTiming?: ProgramAutomationTaskScheduleTiming | null;
}

export interface OneTimeAutomatedMessagesFormModel extends BaseAutomatedMessagesFormModel {
  messageType?: AutomationMessageType.OneTime;
}

export interface RecurringAutomatedMessagesFormModel extends BaseAutomatedMessagesFormModel {
  messageType: AutomationMessageType.Recurring;
  recurring: {
    cycleType: CycleSurveyType;
    count: number;
    unit: SurveyIntervalUnit;
  } & CycleSurvey;
}

export type AutomatedMessagesFormModel = OneTimeAutomatedMessagesFormModel | RecurringAutomatedMessagesFormModel;
